/**
 * @ngdoc type
 * @name isValueInValidRange
 * @module number-utils
 * @kind function
 *
 * @param {number} value tested value
 * @param {number} min allowed minimum (left bound)
 * @param {number} max allowed maximum (right bound)
 *
 * @returns {boolean}
 *
 * @description
 * If given value is between two numbers (including edge values) returns true
 * otherwise return false.
 */
export function isValueInValidRange(value: number, min: number, max: number): boolean {
    return value >= min && value <= max;
}

/**
 * @ngdoc type
 * @name calculateValidValueFromRange
 * @module number-utils
 * @kind function
 *
 * @param {number} value tested value
 * @param {number} minAllowedValue allowed minimum
 * @param {number} maxAllowedValue allowed maximum
 * @param {number} fallbackValue? optional fallback value
 *
 * @returns {number} valid value from defined range or fallback value
 *
 * @description
 * If given value is not from defined range return fallback value if defined
 * or valid value from range closest to original value
 */
export function calculateValidValueFromRange(value: number, minAllowedValue: number, maxAllowedValue: number, fallbackValue?: number): number {

    if (isValueInValidRange(value, minAllowedValue, maxAllowedValue)) {
        return value;
    }

    if (fallbackValue !== undefined) {
        return fallbackValue;
    }

    if (value < minAllowedValue) {
        return minAllowedValue;
    } else {
        return maxAllowedValue;
    }
}

/**
 * Clamps a number within the inclusive range specified by `min` and `max`.
 *
 * @param {number} value - The number to clamp.
 * @param {number} min - The lower bound of the range.
 * @param {number} max - The upper bound of the range.
 * @returns {number} - The clamped number, ensuring it is between `min` and `max`.
 *
 * @example
 * clamp(10, 1, 5); // returns 5
 * clamp(-2, 0, 3); // returns 0
 * clamp(2, 1, 3);  // returns 2
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function assertIsNonNegative(value: number) {
    if (value < 0) {
        throw new Error(`Value of ${value} is negative (less than 0)`);
    }
}

export function isNumber(value: unknown): value is number {
    return typeof value === 'number';
}

// https://observablehq.com/@mbostock/localized-number-parsing
export function fromLocaleStringNumberParser(locale: string): (_: string) => number {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const numerals = [...new Intl.NumberFormat(locale, { useGrouping: false }).format(9876543210)].reverse();
    const index = new Map(numerals.map((d, i) => [d, i]));

    const groupSymbol = parts.find((d) => d.type === 'group')?.value ?? '';
    const decimalSymbol = parts.find((d) => d.type === 'decimal')?.value ?? '';

    const groupRegex = new RegExp(`[${groupSymbol}]`, 'g');
    const decimalRegex = new RegExp(`[${decimalSymbol}]`);
    const numeralRegex = new RegExp(`[${numerals.join('')}]`, 'g');
    const getIndex = (d: string) => index.get(d)?.toString() ?? '';

    function parse(input: string): number {
        const normalized = input
            .trim()
            .replace(groupRegex, '')
            .replace(decimalRegex, '.')
            .replace(numeralRegex, getIndex);

        return normalized ? Number.parseFloat(normalized) : NaN;
    }

    return parse;
}